import { useEffect, useState, useCallback, useMemo } from "react";
import { usePeople } from "@/entities/people/hooks";
import { getFullName } from "@/entities/people/helpers";

export interface PersonEmailData {
  name: string;
  email: string;
}

export const usePeopleData = (peopleIds: string[]) => {
  const [peopleEmails, setPeopleEmails] = useState<(PersonEmailData | null)[]>(
    []
  );
  const [participantNames, setParticipantNames] = useState<string[]>([]);

  const { entities: people } = usePeople(peopleIds);

  const peopleIdsKey = useMemo(() => peopleIds.join(","), [peopleIds]);

  useEffect(() => {
    if (peopleIds.length === 0) {
      setPeopleEmails([]);
      setParticipantNames([]);
    } else {
      const emailData = people.map((person) => {
        if (!person) return null;
        return {
          name: getFullName(person),
          email: person?.email || ""
        };
      });

      const names = people
        .filter((person): person is NonNullable<typeof person> =>
          Boolean(person)
        )
        .map((person) => getFullName(person));

      setPeopleEmails((prevData) => {
        if (
          prevData.length !== emailData.length ||
          !prevData.every((item, index) => {
            const newItem = emailData[index];
            return (
              (item === null && newItem === null) ||
              (item !== null &&
                newItem !== null &&
                item.name === newItem.name &&
                item.email === newItem.email)
            );
          })
        ) {
          return emailData;
        }
        return prevData;
      });

      setParticipantNames((prevNames) => {
        if (
          prevNames.length !== names.length ||
          !prevNames.every((name, index) => name === names[index])
        ) {
          return names;
        }
        return prevNames;
      });
    }
  }, [peopleIdsKey, people.length]);

  const resetPeopleData = useCallback(() => {
    setPeopleEmails([]);
    setParticipantNames([]);
  }, []);

  const resetParticipantNames = useCallback(() => {
    setParticipantNames([]);
  }, []);

  return {
    peopleEmails,
    participantNames,
    resetPeopleData,
    resetParticipantNames
  };
};
